import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  calendarApi,
  homeQuery,
  integrationKey,
  type CalendarEvent,
  type MeetingPayload,
  type CalendarDraft,
} from '../../lib/calendar-api';
import styles from './Integrations.module.css';
function instant(value: string, timezone: string) {
  try {
    return new Date(value).toLocaleString([], {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}
function localInput(value: string, timezone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(value));
    const get = (name: string) => parts.find((p) => p.type === name)?.value;
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  } catch {
    return value.slice(0, 16);
  }
}
export function MeetingCard({
  event,
  timezone,
}: {
  event: CalendarEvent;
  timezone: string;
}) {
  const data = event.payload,
    allDay = data.start?.date;
  return (
    <article className={styles.card}>
      <p className={styles.meta}>Google Calendar · {timezone}</p>
      <h3>{data.summary || 'Meeting'}</h3>
      {allDay ? (
        <p>
          {allDay} to {data.end?.date} (end date exclusive)
        </p>
      ) : (
        <p>
          {instant(event.startsAt, timezone)} –{' '}
          {instant(event.endsAt, timezone)}
          {Date.parse(event.startsAt) <= Date.now() &&
          Date.parse(event.endsAt) > Date.now()
            ? ' · Now'
            : ''}
        </p>
      )}
      {data.organizer?.email && <p>Organizer: {data.organizer.email}</p>}
      <p>
        {data.attendees
          ?.map(
            (p) =>
              `${p.email ?? ''}${p.self ? ` (you: ${p.responseStatus ?? 'needsAction'})` : ''}`,
          )
          .join(', ')}
      </p>
      {data.location && <p>{data.location}</p>}
      {data.description && (
        <details>
          <summary>Description</summary>
          <p>{data.description}</p>
        </details>
      )}
      {data.hangoutLink && /^https:\/\//.test(data.hangoutLink) && (
        <p>
          <a href={data.hangoutLink} target="_blank" rel="noreferrer">
            Join meeting
          </a>
        </p>
      )}
      {data.htmlLink &&
        /^https:\/\/([a-z0-9-]+\.)?google\.com\//.test(data.htmlLink) && (
          <a href={data.htmlLink} target="_blank" rel="noreferrer">
            Open in Google Calendar
          </a>
        )}
    </article>
  );
}
export function CalendarCard({
  eventId,
  draftId,
}: {
  eventId?: string | undefined;
  draftId?: string | undefined;
}) {
  const home = useQuery(homeQuery());
  const query = useQuery<CalendarEvent | CalendarDraft>({
    queryKey: [...integrationKey(), 'card', draftId ?? eventId],
    queryFn: () =>
      draftId ? calendarApi.draft(draftId) : calendarApi.event(eventId!),
    retry: false,
    refetchInterval: 5000,
  });
  if (query.isLoading) return <p role="status">Loading meeting…</p>;
  if (query.isError || !query.data) return <p>This meeting is unavailable.</p>;
  if (draftId && (query.data as CalendarDraft).status === 'dismissed')
    return null;
  return draftId ? (
    <DraftEditor draft={query.data as CalendarDraft} />
  ) : (
    <MeetingCard
      event={query.data as CalendarEvent}
      timezone={home.data?.preferences.timezone ?? 'UTC'}
    />
  );
}
function DraftEditor({ draft }: { draft: CalendarDraft }) {
  const client = useQueryClient(),
    editorKey = [...integrationKey(), 'editor', draft.id];
  const cached = client.getQueryData<{
    payload: MeetingPayload;
    revision: number;
    inviteeText?: string;
  }>(editorKey);
  const [edit, setEdit] = useState<MeetingPayload | undefined>(cached?.payload),
    base = useRef(cached?.revision ?? draft.revision);
  const [inviteeText, setInviteeText] = useState(
    cached?.inviteeText ?? draft.payload.invitees.join(', '),
  );
  const key = useRef(new Map<number, string>());
  useEffect(() => {
    if (edit)
      client.setQueryData(editorKey, {
        payload: edit,
        revision: base.current,
        inviteeText,
      });
    else client.removeQueries({ queryKey: editorKey, exact: true });
  }, [edit, inviteeText]);
  const action = useMutation({
    mutationFn: async (kind: string) => {
      if (kind === 'save' && edit) {
        await calendarApi.edit(draft.id, base.current, edit);
        setEdit(undefined);
      }
      if (kind === 'dismiss') await calendarApi.dismiss(draft.id);
      if (kind === 'schedule') {
        let value = key.current.get(draft.revision);
        if (!value) {
          value = crypto.randomUUID();
          key.current.set(draft.revision, value);
        }
        return calendarApi.schedule(draft.id, draft.revision, value);
      }
      if (kind === 'reconcile' && draft.attempt)
        return calendarApi.reconcile(draft.attempt.id);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: integrationKey() }),
  });
  const data = edit ?? draft.payload,
    attempt = draft.attempt;
  const submitted = ['prepared', 'submitting', 'unknown', 'succeeded'].includes(
    attempt?.status ?? '',
  );
  const field = (name: keyof MeetingPayload, value: string) => {
    if (!edit) {
      base.current = draft.revision;
      setInviteeText(draft.payload.invitees.join(', '));
    }
    if (name === 'invitees') setInviteeText(value);
    setEdit((previous) => ({
      ...(previous ?? draft.payload),
      [name]:
        name === 'invitees'
          ? value
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean)
          : value,
    }));
  };
  const disabled =
    submitted || action.isPending || draft.status === 'dismissed';
  if (attempt?.status === 'succeeded')
    return (
      <article className={styles.card}>
        <h3>Scheduled</h3>
        <p>
          {data.title} · {instant(data.start, data.timezone)}
        </p>
        <p>{data.invitees.join(', ')}</p>
        <p>Google accepted the meeting and invitation request.</p>
        {attempt.receipt?.htmlLink &&
          /^https:\/\//.test(attempt.receipt.htmlLink) && (
            <a href={attempt.receipt.htmlLink} target="_blank" rel="noreferrer">
              Open in Google Calendar
            </a>
          )}
      </article>
    );
  return (
    <article className={styles.card}>
      <p className={styles.meta}>
        Meeting draft · {draft.accountEmail} · Primary calendar
      </p>
      <div className={styles.fields}>
        <label>
          Title
          <input
            value={data.title}
            disabled={disabled}
            onChange={(e) => field('title', e.target.value)}
          />
        </label>
        <label>
          Invitees (email addresses)
          <input
            value={edit ? inviteeText : data.invitees.join(', ')}
            disabled={disabled}
            onChange={(e) => field('invitees', e.target.value)}
          />
        </label>
        <label>
          Start
          <input
            type="datetime-local"
            value={
              data.start.endsWith('Z')
                ? localInput(data.start, data.timezone)
                : data.start
            }
            disabled={disabled}
            onChange={(e) => field('start', e.target.value)}
          />
        </label>
        <label>
          End
          <input
            type="datetime-local"
            value={
              data.end.endsWith('Z')
                ? localInput(data.end, data.timezone)
                : data.end
            }
            disabled={disabled}
            onChange={(e) => field('end', e.target.value)}
          />
        </label>
        <label>
          Timezone
          <input
            value={data.timezone}
            disabled={disabled}
            onChange={(e) => field('timezone', e.target.value)}
          />
        </label>
        <label>
          Location / meeting link
          <input
            value={data.location}
            disabled={disabled}
            onChange={(e) => field('location', e.target.value)}
          />
        </label>
        <label>
          Description
          <textarea
            value={data.description}
            disabled={disabled}
            onChange={(e) => field('description', e.target.value)}
          />
        </label>
      </div>
      {!edit && (
        <p>
          {Math.round((Date.parse(data.end) - Date.parse(data.start)) / 60000)}{' '}
          minutes · {data.timezone}
        </p>
      )}
      <div className={styles.actions}>
        {edit ? (
          <>
            <button disabled={disabled} onClick={() => action.mutate('save')}>
              Save
            </button>
            <button
              disabled={action.isPending}
              onClick={() => setEdit(undefined)}
            >
              Cancel edits
            </button>
          </>
        ) : (
          <>
            <button
              disabled={disabled}
              onClick={() => action.mutate('schedule')}
            >
              Schedule
            </button>
            <button
              disabled={disabled}
              onClick={() => action.mutate('dismiss')}
            >
              Dismiss
            </button>
          </>
        )}
      </div>
      {submitted && (
        <p role="status">
          {attempt?.status === 'unknown'
            ? 'Scheduling status unknown. Check Google Calendar; a new submission is blocked.'
            : 'Scheduling…'}
        </p>
      )}
      {attempt?.status === 'unknown' && (
        <button
          disabled={action.isPending}
          onClick={() => action.mutate('reconcile')}
        >
          Check status
        </button>
      )}
      {attempt?.status === 'failed' && (
        <p>
          Scheduling failed. Review and save this draft before trying again.
        </p>
      )}
      {action.error && (
        <p role="alert">{action.error.message} Your edits are kept.</p>
      )}
    </article>
  );
}
